import { isUndefined, uniq } from 'lodash/fp';
import Fuse from 'fuse.js';
import { TextItem } from 'react-pdf';

import { PdfPageText } from './types';

export const highlightFunction = (snippet: string, childNodeArray: string[], startingNodeIndex = 0) => {

    let allFound: boolean | undefined = undefined;
    let highlightIndex: number[] = [];
    let nodeCharacterIndex: number[] = [];
    let currentNodeIndex = startingNodeIndex;
    let currentSplitNodeIndex = 0;
    let currentFoundString = '';
    let snippetIndex = 0;

    const addHighlightIndex = (currentLetter: string) => {
        highlightIndex = [...highlightIndex, currentNodeIndex];
        snippetIndex = snippetIndex + 1;
        currentFoundString = currentFoundString.concat(currentLetter);
    };
    const moveToNextLetter = () => {
        nodeCharacterIndex = [...nodeCharacterIndex, currentSplitNodeIndex];
        currentSplitNodeIndex = currentSplitNodeIndex + 1;
    };
    const moveToNextNode = () => {
        currentSplitNodeIndex = 0;
        nodeCharacterIndex = [];
        currentNodeIndex = currentNodeIndex + 1;
    };

    const splitSnippet = snippet.split('');
    const allSplitText = childNodeArray.map(node => node.toLowerCase().split(''));
    while (isUndefined(allFound) && currentNodeIndex <= (childNodeArray.length - 1)) {
        const someCharactersAlreadyFound = highlightIndex.length > 0;
        const isEmptyNode = isUndefined(childNodeArray[currentNodeIndex][0]);

        if (currentFoundString.replace(/[^a-zA-Z0-9+()]/g, '') === snippet.replace(/[^a-zA-Z0-9+()]/g, '')) {
            // we have found everything so no need to go through any more nodes
            allFound = true;
            return highlightIndex;
        }

        if (currentNodeIndex === (childNodeArray.length - 1) && isEmptyNode) {
            // then we are still searching, we are in the last node but its an empty one, therefore we haven't found it so exit with all found false
            allFound = false;
            return highlightIndex;
        }
        if (isEmptyNode) {
            if (someCharactersAlreadyFound && (highlightIndex[highlightIndex.length - 1] + 1) === currentNodeIndex) {
                // then we have found some of the string, this is the next node to the last found index and we should include this whitespace and move to the next node
                highlightIndex = [...highlightIndex, currentNodeIndex];
                currentNodeIndex = currentNodeIndex + 1;
            } else if (someCharactersAlreadyFound && (highlightIndex[highlightIndex.length - 1] + 1) !== currentNodeIndex) {
                // then we have found some of the string, this node is not the next along from the last found index so we should reset the results and move to the next node
                currentNodeIndex = currentNodeIndex + 1;
                highlightIndex = [];
                snippetIndex = 0;
                currentSplitNodeIndex = 0;
                currentFoundString = '';
            } else {
                // we have not found any matches yet, this node is empty so just move to the next one
                currentNodeIndex = currentNodeIndex + 1;
            }
        }
        const canMoveToNextLetter = !isUndefined(allSplitText[currentNodeIndex][currentSplitNodeIndex + 1]);

        if ((currentNodeIndex === childNodeArray.length - 1) && !canMoveToNextLetter) {
            // Then we have searched every character and have not found a match for all, exit and return false
            highlightIndex = [];
            snippetIndex = 0;
            currentFoundString = '';
            allFound = false;
        }
        const currentCharacterToMatch = splitSnippet[snippetIndex];
        const currentLetter = allSplitText[currentNodeIndex][currentSplitNodeIndex];
        const previousFoundCharacterNodeIndex = highlightIndex[highlightIndex.length - 1] || 0;
        // replacing the non letters/numbers/parenthesis as string matching was not working for some special characters
        const replacedSearchLetter = currentCharacterToMatch.replace(/[^a-zA-Z0-9+()]/g, '');
        const replacedCurrentLetter = currentLetter.replace(/[^a-zA-Z0-9+()]/g, '');

        if (replacedCurrentLetter !== replacedSearchLetter) {
            // The characters do not match, check if we have already found some that match
            if (someCharactersAlreadyFound) {
                // then we have already found some matching characters, though this character doesn't match which means we have not found the whole string so start again
                highlightIndex = [];
                snippetIndex = 0;
                currentFoundString = '';
            } else {
                // we haven't found any yet so just move to the next one
                if (canMoveToNextLetter) {
                    // move to the next character in this node
                    currentSplitNodeIndex = currentSplitNodeIndex + 1;
                } else {
                    // move to the next node and check the first character
                    currentSplitNodeIndex = 0;
                    currentNodeIndex = currentNodeIndex + 1;
                }
            }
        } else {
            // we have found a matching character
            if (someCharactersAlreadyFound) {
                // we now need to check the node index that the character was found in, it must either be the same node index as the previously found character or the node directly after
                if (previousFoundCharacterNodeIndex !== currentNodeIndex && previousFoundCharacterNodeIndex + 1 !== currentNodeIndex) {
                    // then we have found a match for the next letter but its not in the same/or next node so we need to start looking for the first letter again but from this node and position
                    highlightIndex = [];
                    snippetIndex = 0;
                    currentFoundString = '';
                } else {
                    if (previousFoundCharacterNodeIndex + 1 === currentNodeIndex) {
                        // then we are in the next node along, if the first character does not match then we have not found the right string so reset
                        if (currentSplitNodeIndex !== 0) {
                            highlightIndex = [];
                            snippetIndex = 0;
                            currentFoundString = '';
                        }
                        if (currentSplitNodeIndex === 0) {
                            // then its the first character in the next node and we have matched, check if we can move to the next letter or if we need to move to the next node
                            addHighlightIndex(currentLetter);
                            canMoveToNextLetter ? moveToNextLetter() : moveToNextNode();
                        }
                    }
                    if (previousFoundCharacterNodeIndex === currentNodeIndex) {
                        // then we are in the same node and need to check the index of the current character is +1 from the last found
                        const matchedLetterIsTheNextOne = nodeCharacterIndex[nodeCharacterIndex.length - 1] + 1 === currentSplitNodeIndex;
                        if (!matchedLetterIsTheNextOne) {
                            // we have found the next letter but it wasn't next to the previous one so we need to start the search again
                            highlightIndex = [];
                            snippetIndex = 0;
                            currentFoundString = '';
                        } else {
                            addHighlightIndex(currentLetter);
                            canMoveToNextLetter ? moveToNextLetter() : moveToNextNode();
                        }
                    }
                }
            } else {
                // We have found a match for the first character of the snippet
                addHighlightIndex(currentLetter);
                canMoveToNextLetter ? moveToNextLetter() : moveToNextNode();
            }
        }
    }
    return highlightIndex;
};

export interface SentenceIndexes {
    nodeIndexes: number[];
    sentence: string;
}

export const splitPdfText = (textItems: TextItem[]): SentenceIndexes[] => {
    const pageText = textItems.map(({ str, hasEOL }) => ({ str, hasEOL }));

    const punctuationMarks: string[] = ['.', '!', ';', ':', ','];
    const sentenceSplitRegex = new RegExp('(?<=[.!;:,])\\s+');

    const containsPunctuation = (text: string, punctuation: string[]): boolean => punctuation.some(mark => text.includes(mark));

    // trimEnd removes any whitespace from the end of the string so we can check the last character
    const endsWithPunctuation = (sentence: string): boolean => /[.!;:,]$/.test(sentence.trimEnd());

    const result: SentenceIndexes[] = [];
    let currentSentence = '';
    let currentIndexes: number[] = [];

    for (let i = 0; i < pageText.length; i++) {
        const { str, hasEOL } = pageText[i];

        if (containsPunctuation(str, punctuationMarks)) {
            // then we should look to break up the node into its sentences
            const nodeSentences = str.split(sentenceSplitRegex);

            nodeSentences.map(sentence => {
                currentSentence = currentSentence.concat(sentence === '' ? ' ' : sentence);
                currentIndexes = [...currentIndexes, i];
                if (endsWithPunctuation(currentSentence)) {
                    // if the sentence ends with punctuation (which all should except the last one which might not), then we push this to the results along with the node indexes (we might be finishing off a sentence from a previous node or just adding one from this node).
                    result.push({
                        sentence: currentSentence.toLowerCase(),
                        nodeIndexes: currentIndexes
                    });
                    // reset the currents
                    currentSentence = '';
                    currentIndexes = [];
                }
            });
        } else {
            // no punctuation therefore we add it to the current sentence and node index array as it must be in the middle of a sentence or a whitespace between words
            currentSentence = currentSentence.concat(str === '' ? ' ' : str);
            currentIndexes = [...currentIndexes, i];
            if (hasEOL) {
                // If this an end of line node, then we cut the sentence here as some parts of these legal documents span multiple lines without punctuation.
                // Though the layout of the document would suggest that we are to read them separately, such as lines of addresses. Doing this helps reduce the number indexes highlighted while creating our own sentences.
                result.push({
                    sentence: currentSentence.toLowerCase(),
                    nodeIndexes: currentIndexes
                });
                // reset the currents
                currentSentence = '';
                currentIndexes = [];
            }
        }
    }

    // If we have mapped through all of the nodes and the last node does not contain any matching characters we should push the results anyway to capture the remaining sentence
    if (currentSentence) {
        result.push({
            sentence: currentSentence,
            nodeIndexes: [...currentIndexes],
        });
    }
    return result;
};

const getFuzzyMatchHighlights = (textItems: TextItem[], searchTerm: string) => {
    const splitPageText = splitPdfText(textItems);

    const fuzzyMatchOptions = {
        includeScore: true,
        threshold: 0.3,
        keys: ['sentence'],
        shouldSort: true
    };

    const highlightFuse = new Fuse(splitPageText, fuzzyMatchOptions);
    const fuzzyMatches: Fuse.FuseResult<SentenceIndexes>[] = highlightFuse.search(searchTerm);
    const results = fuzzyMatches.map(({ item, score }) => ({ value: item.sentence, indexes: item.nodeIndexes, score }));
    let searchHighlightIndexes: number[] = [];
    if (results.length > 0) {
        searchHighlightIndexes = uniq(results[0].indexes);
    }
    return searchHighlightIndexes;
};

interface IndexedTextItem extends TextItem {
    index: number;
}

export const getHighlightIndexesForPageText = (snippet: string, pageText: PdfPageText) => {
    const snippetToCheck = snippet!.replace(/\s/g, '').toLowerCase();
    const textToSearch = pageText.textItems.map(({ str }) => str.replace(/\s/g, ''));
    const fuzzyMatchOptions = {
        includeScore: true,
        threshold: 0.5,
        keys: ['str'],
        shouldSort: true
    };
    const indexedTextItem: IndexedTextItem[] = pageText.textItems.map((item, index) => ({ ...item, index }));
    const highlightFuse = new Fuse(indexedTextItem, fuzzyMatchOptions);
    const fuzzyMatches: Fuse.FuseResult<IndexedTextItem>[] = highlightFuse.search(snippet!);
    const results = fuzzyMatches.map(({ item, score }) => ({ value: item.str, index: item.index, score: score! }));
    let startingIndex = 0;
    let highlightIndexes: number[] = [];
    let isFuzzyMatchHighlight = false;

    if (results.length > 0) {
        const closestMatchNodeIndex = results[0].index;
        if (closestMatchNodeIndex > 4) {
            // minus a couple of indexes to allow for fuzzy match ability
            startingIndex = closestMatchNodeIndex - 4;
        }
        highlightIndexes = highlightFunction(snippetToCheck, textToSearch, startingIndex);
    }
    if (highlightIndexes.length === 0) {
        // try again with the whole page as the fuzzy match hasn't returned any results
        highlightIndexes = highlightFunction(snippetToCheck, textToSearch, 0);
    }
    if (highlightIndexes.length === 0) {
        // then we still haven't found an exact match so try with a fuzzy match
        highlightIndexes = getFuzzyMatchHighlights(pageText.textItems, snippet);
        isFuzzyMatchHighlight = true;
    }
    const uniqueIndexes = uniq(highlightIndexes);
    return { uniqueIndexes, isFuzzyMatchHighlight };
};
